import { AutoFillDefinition, DefaultField, Field } from '../Models';
import { SourceType } from '../Models/configuration-options/source-type';
import { ProcessType } from '../Models/configuration-options/process-type';
import { IAutoFillFieldMapping, IAutoFillPropertyMapping } from '../Models/ModuleDefinitions/auto-fill-field-mapping';
import { BasicField } from '../Models/ModuleDefinitions/basic-field';
import { IPreviewField } from '../Models/ModuleDefinitions/preview-field';

export class AutoFillDefinitionBuilder {
    private autofillDefinition: AutoFillDefinition;
    constructor() {
        this.autofillDefinition = new AutoFillDefinition();
    }

    addFeedLookupData(feedName: string,matchType: string): AutoFillDefinitionBuilder {
        this.autofillDefinition.LookupTableName = feedName;
        this.autofillDefinition.MatchType = matchType;
        return this;
    }

    addValidationHandlingOptions(
        failValidationOnFailedLookup: boolean,
        publishOnFailedLookup: boolean,
        allowMappingAfterFirstSave: boolean): AutoFillDefinitionBuilder {
        this.autofillDefinition.FailValidationOnFailedLookup = failValidationOnFailedLookup;
        this.autofillDefinition.PublishAnywayOnFailedLookup = publishOnFailedLookup;
        this.autofillDefinition.AllowMappingAfterFirstSave = allowMappingAfterFirstSave;

        return this;
    }

    addQueryIdentifier(moduleId: string, queryId: string): AutoFillDefinitionBuilder {
        this.autofillDefinition.QueryModuleId = moduleId;
        this.autofillDefinition.QueryId = queryId;
        return this;
    }

    addDefaultName(name: string): AutoFillDefinitionBuilder {
        this.autofillDefinition.DefaultName = name;
        return this;
    }

    addPreviewFields(previewFields: IPreviewField[]): AutoFillDefinitionBuilder {
        this.autofillDefinition.PreviewFields = previewFields
            .filter(previewField => previewField.lookupField)
            .reduce((mappedPreviewFields, previewField) => {
                mappedPreviewFields[previewField.label] = previewField.lookupField.name;
                return mappedPreviewFields;
            },
                {}
            );
        return this;
    }

    addControlValueMappings(
        autoFillMappings: IAutoFillFieldMapping[],
        lookupFields: BasicField[],
        sourceType: SourceType,
        processType: ProcessType): AutoFillDefinitionBuilder {

        this.populateHiddenFieldPropertyMappings(autoFillMappings, lookupFields, sourceType, processType);

        this.autofillDefinition.ControlValueMappings = autoFillMappings.reduce(
            (fieldMappings, fieldMapping) => {
                fieldMappings[fieldMapping.field.FieldId] = fieldMapping.propertyMappings
                    .filter(propertyMapping => propertyMapping.lookupField)
                    .reduce(
                        (propertyMappings, propertyMapping) => {
                            let lookupFieldId = propertyMapping.lookupField.name;
                            if (propertyMapping.lookupField.dataType === 'hierarchy') {
                                lookupFieldId += 'Id';
                            }
                            const fieldProperty = propertyMapping.fieldProperty;
                            if (sourceType === SourceType.LinkedRecord && processType === ProcessType.server) {
                                const sourceField = propertyMapping.lookupField;
                                const sourceFieldMapping = (sourceField.dataType === 'calc') ?
                                    sourceField.mappings.find((mapping) => mapping.controlPropertyName === 'DisplayValue') :
                                    sourceField.mappings.find((mapping) => mapping.controlPropertyName === fieldProperty);

                                    lookupFieldId = sourceFieldMapping?.modelPropertyName;
                            }
                            propertyMappings[fieldProperty] = lookupFieldId;
                            return propertyMappings;
                        },
                        {});
                return fieldMappings;
            },
            {}
        );
        return this;
    }

    enabledWhen(enabledWhenField: Field, enabledWhenValue: string): AutoFillDefinitionBuilder {
        if (!enabledWhenField) {
            this.autofillDefinition.EnabledWhen = {};
            return this;
        }

        this.autofillDefinition.EnabledWhen = { [enabledWhenField.FieldId]: enabledWhenValue };
        return this;
    }

    isEnabledOffline(enabledOffline: boolean): AutoFillDefinitionBuilder {
        this.autofillDefinition.IsEnabledOffline = enabledOffline;
        return this;
    }

    addDefaultField(defaultField: string): AutoFillDefinitionBuilder {
        if (defaultField !== DefaultField.None) {
            this.autofillDefinition.DefaultField = defaultField;
        }

        return this;
    }

    setProcessingType(processType: ProcessType): AutoFillDefinitionBuilder {
        if (processType !== undefined && processType !== null) {
            this.autofillDefinition.ProcessType = processType;
        }

        return this;
    }

    build(): AutoFillDefinition {
        return this.autofillDefinition;
    }

    private populateHiddenFieldPropertyMappings(
        autoFillMappings: IAutoFillFieldMapping[],
        lookupFields: BasicField[],
        sourceType: SourceType,
        processType: ProcessType): void {

        if(this.isClientSideLinkedRecord(sourceType, processType)){
            return;
        }

        for (const fieldMapping of autoFillMappings) {
            if(fieldMapping.field.FieldUniqueId === 'datetimepicker') {
                this.addMappingsForDateField(fieldMapping, sourceType, lookupFields);
                continue;
            }
            this.addMappingsForOtherFieldTypes(fieldMapping, lookupFields);
        }
    }

    private addMappingsForDateField(fieldMapping: IAutoFillFieldMapping, sourceType: SourceType, lookupFields: BasicField[]) {
        const matchingSourceField = this.findMatchingSourceDateField(fieldMapping.propertyMappings, lookupFields);
        //clone to ensure we have a copy before it's altered
        const orginalSourceField =  this.makeDirtyDeepClone(matchingSourceField);

        for (const dateSourcePropertyMapping of fieldMapping.propertyMappings) {
            const fieldProperty = dateSourcePropertyMapping.fieldProperty;

            if(!this.datePropertyMappingRequiresHiddenMappings(fieldMapping.field, sourceType, fieldProperty)) {
                continue;
            }

            const sourceFieldProperty = matchingSourceField.mappings.filter(mapping => mapping.controlPropertyName === fieldProperty)[0];

            if(!sourceFieldProperty){
                continue;
            }

            const fieldCopy =  this.makeDirtyDeepClone(orginalSourceField); // dirty deep clone
            fieldCopy.name = sourceFieldProperty.modelPropertyName;
            dateSourcePropertyMapping.lookupField = fieldCopy;
        }
    }

    private addMappingsForOtherFieldTypes(fieldMapping: IAutoFillFieldMapping, lookupFields: BasicField[]) {
        for (const sourcePropertyMapping of fieldMapping.propertyMappings) {
            switch (sourcePropertyMapping.fieldProperty) {
                case 'SelectedText':
                    const matchingSourceField = this.findMatchingSourceField(lookupFields, sourcePropertyMapping.lookupField.name);
                    const matchingSourceFieldCopy = this.makeDirtyDeepClone(matchingSourceField);

                    if (!matchingSourceField.name.endsWith('Alias')) {
                        matchingSourceFieldCopy.name = matchingSourceField.name + 'Alias';
                    }

                    fieldMapping
                        .propertyMappings
                        .filter(targetPropertyMapping => targetPropertyMapping.fieldProperty === 'SelectedItemAsString')[0]
                        .lookupField = matchingSourceFieldCopy;
                    break;
            }
        }
    }

    private makeDirtyDeepClone(field: BasicField) {
        return JSON.parse(JSON.stringify(field));
    }

    private datePropertyMappingRequiresHiddenMappings(targetField: Field, sourceType: SourceType, fieldProperty: string) {
        if ((sourceType === SourceType.LinkedRecord && fieldProperty === 'Date1')) {
             return false;
        }

        if (!targetField.Mappings.some(mp => mp.ControlPropertyName === fieldProperty)) {
            return false;
        }

        return true;
    }

    private findMatchingSourceDateField(propertyMappings: IAutoFillPropertyMapping[], sourceFields: BasicField[]) {
        const datePropertyMapping = propertyMappings.filter(targetPropertyMapping => targetPropertyMapping.fieldProperty === 'Date1')[0];

        let dateLookupFieldName = datePropertyMapping.lookupField.name;
        const matchingLookupField = this.findMatchingSourceField(sourceFields, dateLookupFieldName);
        if(matchingLookupField){
            return matchingLookupField;
        }
        //Feeds append "date" to the date field, so to make sure we find the matching field we have to remove it.
        dateLookupFieldName = dateLookupFieldName.substring(0, dateLookupFieldName.length - 4);
        return  this.findMatchingSourceField(sourceFields, dateLookupFieldName);
    }

    private findMatchingSourceField(sourceFields: BasicField[], sourceFieldName: string) {
        return sourceFields.filter(lookupField => lookupField.name === sourceFieldName)[0];
    }

    private isClientSideLinkedRecord(sourceType: SourceType, processType: ProcessType) {
        return (sourceType === SourceType.LinkedRecord && processType === ProcessType.client);
    }
}
